import { computed, h, defineComponent, ref } from 'vue';
import { box } from '@/ui/components/Box';
import { pageSubtitle } from '@/ui/components/PageSubtitle';
import { pageTitle } from '@/ui/components/PageTitle';
import { col, cols } from '@/ui/components/Columns';
import { emptyEntry } from '@/ui/pages/EmptyStateEntry';
import { t } from '@/ui/i18n/i18n';
import { button } from '@/ui/components/Button';
import { container, loader, logo } from '@/ui/components';
import { Icon } from '@/ui/components/icon/Icon';
import { useTask } from '@/ui/concerns';
import { pushRoute } from '@/ui/helpers';
import { list as getChecklistList } from '@/services/api/resources/checklist';
import { ChecklistList } from '@/entities/checklist';
import { emptyList } from '@/services/api';
import { Modal } from '@/ui/components/Modal';
import { CLICK_EVENTS, trackClick } from '@/services/activityTracker/activityTrackerHelper';

interface WelcomeFromTemplateBuilderProps {
  active: boolean;
}

export const WelcomeFromTemplateBuilder = defineComponent<WelcomeFromTemplateBuilderProps>({
  name: 'WelcomeFromTemplateBuilder',
  props: ['active'],
  setup(props, context) {
    const list = ref<ChecklistList>(emptyList());
    const getList = useTask(
      () => getChecklistList({ page: 1, perPage: 2, sort: '', dir: '', search: '', category: [], status: null }),
      listResponse => {
        list.value = listResponse;
      },
    );
    const template = computed(() => {
      return list.value.data[0] ?? { data: { title: 'Loading ...' }, id: '1' };
    });
    const onCustomizeTemplateClick = () => {
      trackClick(CLICK_EVENTS.REGISTER_FROM_TEMPLATE.CUSTOMIZE_TEMPLATE);
      pushRoute({
        name: 'checklistEdit',
        params: { id: template.value.id!.toString() },
      });
    };
    const onStartInspectionClick = () => {
      trackClick(CLICK_EVENTS.REGISTER_FROM_TEMPLATE.START_INSPECTION);
      pushRoute({
        name: 'inspectionCreate',
        params: { id: template.value.id!.toString() },
      });
    };

    getList.run();

    return function render() {
      const columns = cols([
        emptyEntry(
          '/images/create_templates.svg',
          t('welcome-from-template.first-step.title'),
          t('welcome-from-template.first-step.explanation'),
          null,
          'empty-state-entry--welcome-from-template',
        ),
        col([
          h(Icon, { props: { icon: 'chevron-right', iconPack: 'far' } }),
        ], true, { 'welcome-from-template__next-step-icon-column': true }),
        emptyEntry(
          '/images/conduct_inspections.svg',
          t('welcome-from-template.second-step.title'),
          t('welcome-from-template.second-step.explanation'),
          null,
          'empty-state-entry--welcome-from-template',
        ),
        col([
          h(Icon, { props: { icon: 'chevron-right', iconPack: 'far' } }),
        ], true, { 'welcome-from-template__next-step-icon-column': true }),
        emptyEntry(
          '/images/analyse_results.svg',
          t('welcome-from-template.third-step.title'),
          t('welcome-from-template.third-step.explanation'),
          null,
          'empty-state-entry--welcome-from-template',
        ),
      ]);

      const CTAs = h('div', { class: 'welcome-from-template__ctas' }, [
        button(t('welcome-from-template.customize-template'), onCustomizeTemplateClick, {}, { outlined: true }),
        button(t('welcome-from-template.start-inspection'), onStartInspectionClick),
      ]);

      return h(Modal, {
        props: {
          active: props.active,
          customClass: 'welcome-from-template',
          trapFocus: true,
          canCancel: ['x'],
        },
        on: {
          close: () => {
            context.emit('close');
          },
        },
      }, [
        container([
          loader('div', getList, [
            h('div', { class: 'welcome-from-template__title-row' }, [
              logo({ customClass: 'logo-img--welcome-from-template' }),
            ]),
            pageSubtitle(t('welcome-from-template.welcome')),
            pageTitle(`${template.value.data.title} ${t('welcome-from-template.is-ready-to-use')}`),
            box([
              pageSubtitle(t('welcome-from-template.subtitle')),
              columns,
            ], { class: 'welcome-from-template__inner-box' }),
            CTAs,
          ]),
        ]),
      ]);
    };
  },
});
